/**
 * LessonsContest — facebook-style invitation visible inside the chat.
 *
 * For verified teachers: a submission form for lessons they teach (or wish
 * they could). Submissions feed the orchestrator pipeline. When teachers see
 * their lessons appear on the platform, they realize TSOGN listened.
 *
 * For non-teachers: a sign that this exists, with a one-click path into the
 * teacher application flow.
 *
 * Methodology hold #5: not a marketing funnel. No "exciting!" copy. Calm.
 */
import { useEffect, useState } from 'react';
import { Sparkles, Send, GraduationCap, Hammer, Loader2, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';

const GRADE_BANDS = ['K-2', '3-5', '6-8', '9-10', '11-12', 'Higher Ed', 'Adult Learner'];

export default function LessonsContest({ onApply }) {
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const [mine, setMine] = useState([]);

  // Form state
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [gradeBand, setGradeBand] = useState('9-10');
  const [subjectArea, setSubjectArea] = useState('');
  const [sourceConstraints, setSourceConstraints] = useState('');
  const [primarySources, setPrimarySources] = useState('');
  const [teachingNotes, setTeachingNotes] = useState('');

  const reload = async () => {
    setBusy(true);
    try {
      const r = await api.get('/teacher/lessons/contest');
      setMeta(r.data);
      if (r.data.is_verified_teacher) {
        try {
          const r2 = await api.get('/teacher/lessons/mine');
          setMine(r2.data || []);
        } catch { setMine([]); }
      }
    } catch (e) {
      setMeta({ is_verified_teacher: false, intro: '', totals: { submitted: 0, in_pipeline: 0, published: 0 }, grade_bands: GRADE_BANDS });
    }
    setBusy(false);
  };
  useEffect(() => { reload(); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setMsg('');
    if (!title || title.length < 4) { setMsg('Title needs at least 4 characters.'); return; }
    if (!summary || summary.length < 20) { setMsg('Summary needs at least 20 characters.'); return; }
    if (!subjectArea) { setMsg('Subject area is required.'); return; }
    setSubmitting(true);
    try {
      const sources = primarySources.split('\n').map((s) => s.trim()).filter(Boolean);
      const r = await api.post('/teacher/lessons/submit', {
        title, summary, grade_band: gradeBand, subject_area: subjectArea,
        source_constraints: sourceConstraints, primary_sources: sources, teaching_notes: teachingNotes,
      });
      setMsg(r.data?.message || 'Submitted.');
      setTitle(''); setSummary(''); setSubjectArea(''); setSourceConstraints('');
      setPrimarySources(''); setTeachingNotes('');
      await reload();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Submit failed.');
    }
    setSubmitting(false);
  };

  if (!meta) {
    return (
      <div className="p-5 md:p-7 max-w-3xl">
        <Loader2 size={18} className="animate-spin text-[#2F8A92]" />
      </div>
    );
  }

  const isVerified = !!meta.is_verified_teacher;

  return (
    <div className="p-5 md:p-7 max-w-3xl" data-testid="lessons-contest">
      <div className="flex items-center gap-2 mb-2">
        <Hammer size={16} className="text-[#D4A84A]" />
        <span className="font-hud text-[10px] md:text-xs text-[#D4A84A] tracking-widest uppercase">Lessons Contest</span>
      </div>
      <h2 className="font-historical text-[#D4A84A] text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-3">
        Tell us what you teach.
      </h2>
      <p className="font-mono text-sm md:text-base text-white/85 leading-relaxed mb-6 max-w-2xl">
        {meta.intro}
      </p>

      <div className="grid grid-cols-3 gap-3 mb-6 text-center" data-testid="lessons-contest-totals">
        {[['Submitted', meta.totals.submitted], ['In pipeline', meta.totals.in_pipeline], ['Published', meta.totals.published]].map(([label, n]) => (
          <div key={label} className="border border-[#2F8A92]/20 rounded p-3">
            <div className="font-historical text-2xl md:text-3xl text-[#D4A84A] font-light">{n}</div>
            <div className="font-hud text-[9px] md:text-[10px] text-white/65 tracking-widest uppercase mt-1">{label}</div>
          </div>
        ))}
      </div>

      {!isVerified && (
        <div className="border border-[#D4A84A]/30 rounded p-5 bg-[#D4A84A]/5" data-testid="lessons-contest-non-teacher">
          <div className="flex items-center gap-2 mb-2">
            <GraduationCap size={14} className="text-[#D4A84A]" />
            <span className="font-hud text-[10px] tracking-widest text-[#D4A84A] uppercase">Teacher access required</span>
          </div>
          <p className="font-mono text-sm text-white/85 leading-relaxed mb-3">
            Submitting to the lessons contest is open to verified teachers. Apply with a school email — we'll verify the institution and the inbox.
          </p>
          <button
            onClick={() => onApply?.()}
            data-testid="lessons-contest-apply-btn"
            className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-4 py-2 hover:bg-[#D4A84A]/85 inline-flex items-center gap-2"
          >
            <Sparkles size={13} /> APPLY FOR TEACHER STATUS
          </button>
        </div>
      )}

      {isVerified && (
        <>
          <form onSubmit={submit} className="space-y-4 mb-7" data-testid="lessons-contest-form">
            <div>
              <label className="font-hud text-[10px] tracking-widest text-white/65 uppercase block mb-1">Lesson title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. The Spice Trade and the Indian Ocean"
                data-testid="lesson-title"
                className="w-full bg-transparent border border-[#2F8A92]/25 rounded px-3 py-2 font-mono text-sm text-white outline-none focus:border-[#2F8A92]/55"
              />
            </div>
            <div>
              <label className="font-hud text-[10px] tracking-widest text-white/65 uppercase block mb-1">Summary</label>
              <textarea
                rows={3}
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="What is this lesson about and what makes it hard to teach as well as you'd like?"
                data-testid="lesson-summary"
                className="w-full bg-transparent border border-[#2F8A92]/25 rounded px-3 py-2 font-mono text-sm text-white outline-none focus:border-[#2F8A92]/55"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="font-hud text-[10px] tracking-widest text-white/65 uppercase block mb-1">Grade band</label>
                <select
                  value={gradeBand}
                  onChange={(e) => setGradeBand(e.target.value)}
                  data-testid="lesson-grade-band"
                  className="w-full bg-[#0A0A0A] border border-[#2F8A92]/25 rounded px-3 py-2 font-mono text-sm text-white outline-none"
                >
                  {(meta.grade_bands || GRADE_BANDS).map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="font-hud text-[10px] tracking-widest text-white/65 uppercase block mb-1">Subject area</label>
                <input
                  type="text"
                  value={subjectArea}
                  onChange={(e) => setSubjectArea(e.target.value)}
                  placeholder="World history / Civics / Economics / …"
                  data-testid="lesson-subject-area"
                  className="w-full bg-transparent border border-[#2F8A92]/25 rounded px-3 py-2 font-mono text-sm text-white outline-none focus:border-[#2F8A92]/55"
                />
              </div>
            </div>
            <div>
              <label className="font-hud text-[10px] tracking-widest text-white/65 uppercase block mb-1">Constraints you face</label>
              <textarea
                rows={2}
                value={sourceConstraints}
                onChange={(e) => setSourceConstraints(e.target.value)}
                placeholder="e.g. district mandates a specific textbook; can't show certain primary sources without sign-off"
                data-testid="lesson-constraints"
                className="w-full bg-transparent border border-[#2F8A92]/25 rounded px-3 py-2 font-mono text-sm text-white outline-none focus:border-[#2F8A92]/55"
              />
            </div>
            <div>
              <label className="font-hud text-[10px] tracking-widest text-white/65 uppercase block mb-1">Primary sources you trust (one URL or citation per line)</label>
              <textarea
                rows={3}
                value={primarySources}
                onChange={(e) => setPrimarySources(e.target.value)}
                placeholder="Library of Congress, JSTOR Open, BHL, archive.org collection links, …"
                data-testid="lesson-primary-sources"
                className="w-full bg-transparent border border-[#2F8A92]/25 rounded px-3 py-2 font-mono text-xs text-white outline-none focus:border-[#2F8A92]/55"
              />
            </div>
            <div>
              <label className="font-hud text-[10px] tracking-widest text-white/65 uppercase block mb-1">Teaching notes</label>
              <textarea
                rows={2}
                value={teachingNotes}
                onChange={(e) => setTeachingNotes(e.target.value)}
                placeholder="What you wish the chain version of this lesson got right…"
                data-testid="lesson-teaching-notes"
                className="w-full bg-transparent border border-[#2F8A92]/25 rounded px-3 py-2 font-mono text-sm text-white outline-none focus:border-[#2F8A92]/55"
              />
            </div>
            {msg && (
              <div className="font-mono text-xs md:text-sm text-white/85 border border-[#2F8A92]/25 rounded p-3 bg-[#2F8A92]/5" data-testid="lesson-submit-result">
                {msg}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="lesson-submit-btn"
              className="font-hud text-xs md:text-sm tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-4 py-2 hover:bg-[#D4A84A]/85 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              SUBMIT LESSON
            </button>
          </form>

          {mine.length > 0 && (
            <div data-testid="lessons-contest-mine">
              <div className="font-hud text-[10px] md:text-xs text-[#2F8A92] tracking-widest uppercase mb-2">My submissions ({mine.length})</div>
              <ul className="space-y-2">
                {mine.map((m) => (
                  <li key={m.id} className="border border-[#2F8A92]/15 rounded p-3">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 size={13} className="text-[#D4A84A] mt-1" />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm text-white truncate">{m.title}</div>
                        <div className="font-mono text-[11px] text-white/55 mt-0.5">
                          {m.grade_band} · {m.subject_area} · <span className="font-hud uppercase tracking-widest text-[10px] text-[#D4A84A]">{m.status}</span>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
